import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";

const TABS = [
  { key: "lavoro",       label: "LAVORO",       cols: ["Anno","Evento","Data","Ore","Contratto"] },
  { key: "servizio",     label: "SERVIZIO",     cols: ["Anno","Dal","Al","Settore","Classe","Ore","Materia"] },
  { key: "titoli",       label: "TITOLI",       cols: ["Data","Titolo","Scuola","Graduatoria","Voto"] },
  { key: "abilitazioni", label: "ABILITAZIONI", cols: ["Data","Descrizione","Nota"] },
  { key: "corsi",        label: "CORSI",        cols: ["Data","Giorni","Corso","Ente","Luogo"] },
  { key: "allegati",     label: "ALLEGATI",     cols: [], isUpload: true },
  { key: "documenti",    label: "DOCUMENTI",    cols: ["Anno","Data","Descrizione","Descrizone File","Path Dcoumenti"] },
  { key: "note",         label: "NOTE",         cols: ["Data","Nota"] },
];

// Campi a testo libero (NO autocomplete dropdown)
const FREE_TEXT = new Set(["Materia","Titolo","Nota","Corso","Descrizione","Descrizone File","Path Dcoumenti"]);

// Campi data (mostrano icona calendario)
const DATE_FIELDS = new Set(["Data","Dal","Al"]);

function formatAnno(v) {
  if (v == null || v === "") return "";
  const s = String(v);
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}/${s.slice(4)}`;
  return s;
}

// Converte dd/mm/yyyy <-> yyyy-mm-dd per input[type=date]. Ritorna null se non è una data valida.
function dmyToIso(dmy) {
  if (!dmy) return null;
  const s = String(dmy).trim();
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/); // anno a 2 cifre
  if (m) {
    const [, d, mo, y] = m;
    const yy = Number(y) < 50 ? `20${y}` : `19${y}`;
    return `${yy}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return null;
}
function isoToDmy(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

// Cache distinct values
const distinctCache = {};

export default function DocenteEventiPage() {
  const { codice } = useParams();
  const navigate = useNavigate();
  const [docente, setDocente] = useState(null);
  const [tab, setTab] = useState("lavoro");
  const [loading, setLoading] = useState(true);
  const [eventi, setEventi] = useState([]);
  const [counts, setCounts] = useState({});
  const [msg, setMsg] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [showModelli, setShowModelli] = useState(false);
  const [showGenera, setShowGenera] = useState(false);

  const fetchEventi = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/docenti/${codice}/eventi`, { params: { categoria: tab } });
      setEventi(res.data.eventi || []);
      setCounts(res.data.counts || {});
      setDocente(res.data.docente || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [codice, tab]);

  useEffect(() => { fetchEventi(); }, [fetchEventi]);
  // reset sorting on tab change
  useEffect(() => { setSortBy(null); setSortDir("asc"); }, [tab]);

  const current = TABS.find(t => t.key === tab) || TABS[0];

  const toggleSort = (f) => {
    if (sortBy === f) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(f); setSortDir("asc"); }
  };
  const sortInd = (f) => sortBy === f ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const sortedEventi = (() => {
    // Default: nel tab SERVIZIO la lista è ordinata per Anno crescente (richiesta utente).
    const effSortBy = sortBy || (tab === "servizio" ? "Anno" : null);
    const effSortDir = sortBy ? sortDir : "asc";
    if (!effSortBy) return eventi;
    const arr = [...eventi];
    const isoFromDmy = (s) => {
      const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(String(s || ""));
      if (!m) return null;
      let [, d, mo, y] = m;
      if (y.length === 2) y = Number(y) < 50 ? `20${y}` : `19${y}`;
      return `${y}-${mo.padStart(2,"0")}-${d.padStart(2,"0")}`;
    };
    const toKey = (v) => {
      if (v == null || v === "") return null;
      // Prova data dd/mm/yyyy
      const iso = isoFromDmy(v);
      if (iso) return iso;
      // Prova numero
      const n = Number(String(v).replace(",", "."));
      if (!Number.isNaN(n)) return n;
      return String(v).toLowerCase();
    };
    arr.sort((a, b) => {
      const av = toKey(a[effSortBy]);
      const bv = toKey(b[effSortBy]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av !== typeof bv) {
        // tipo misto: stringifica
        const as = String(av), bs = String(bv);
        return effSortDir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
      }
      if (av < bv) return effSortDir === "asc" ? -1 : 1;
      if (av > bv) return effSortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  const updateCell = async (chiave, field, value) => {
    // Ottimistic update
    setEventi(prev => prev.map(e => e.ChiavePrimaria === chiave ? { ...e, [field]: value } : e));
    try {
      await api.put(`/docenti-eventi/${chiave}`, { [field]: value });
      // Invalida cache distinct di quel campo
      delete distinctCache[`${field}_${tab}`];
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || "Errore salvataggio" });
      fetchEventi();
    }
  };

  const deleteRow = async (chiave) => {
    try {
      await api.delete(`/docenti-eventi/${chiave}`);
      setEventi(prev => prev.filter(e => e.ChiavePrimaria !== chiave));
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || "Errore" });
    }
  };

  const addRow = async () => {
    try {
      const res = await api.post("/docenti-eventi", { codice_gestito: Number(codice), categoria: tab });
      setEventi(prev => [...prev, res.data.data]);
      setMsg({ ok: true, text: "Riga aggiunta" });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || "Errore" });
    }
  };

  const cleanupDuplicati = async () => {
    try {
      // 1) Dry-run per vedere quanti duplicati ci sono globalmente
      const dry = await api.post("/docenti-eventi/dedupe", null, { params: { dry_run: true } });
      const groups = dry.data.groups_with_duplicates || 0;
      const toDel = dry.data.records_to_delete || 0;
      if (toDel === 0) {
        setMsg({ ok: true, text: "Nessun duplicato trovato negli archivi docenti." });
        return;
      }
      const ok = window.confirm(
        `Trovati ${groups} gruppi di righe duplicate negli archivi docenti.\n` +
        `Verranno eliminate ${toDel} righe (mantenendo la prima copia di ogni gruppo).\n\n` +
        `Confermi l'eliminazione definitiva?`
      );
      if (!ok) return;
      // 2) Esegui veramente
      const r = await api.post("/docenti-eventi/dedupe", null, { params: { dry_run: false } });
      setMsg({ ok: true, text: `Rimosse ${r.data.deleted_eventi || 0} righe duplicate (${r.data.groups_with_duplicates || 0} gruppi).` });
      fetchEventi();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || "Errore durante la pulizia duplicati" });
    }
  };

  return (
    <div data-testid="docente-eventi-page">
      <div className="sw-page-title">
        <h1>Scheda Docente - Eventi</h1>
        <div className="sw-breadcrumb">Home &gt; Docenti &gt; Scheda</div>
      </div>

      <div style={{ padding: "4px 20px 0" }}>
        <button onClick={() => navigate("/g/docenti/docenti")} className="sw-filter-btn" style={{ marginBottom: 10 }}>← Torna all'elenco</button>
      </div>

      {/* Header docente */}
      <div style={{ padding: "0 20px 18px", display: "flex", gap: 18, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "white", background: "#0288d1", padding: "6px 14px", display: "block", textAlign: "center", letterSpacing: 0.5 }}>CODICE DOCENTE</div>
          <div style={{ fontSize: 22, fontWeight: 700, border: "1px solid #0288d1", padding: "10px 22px", fontFamily: "Consolas, monospace", height: 48, lineHeight: "28px", minWidth: 140, textAlign: "center", boxSizing: "border-box" }}>{codice}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "white", background: "#0288d1", padding: "6px 14px", display: "block", letterSpacing: 0.5 }}>COGNOME NOME</div>
          <div style={{ fontSize: 22, fontWeight: 700, border: "1px solid #0288d1", padding: "10px 22px", height: 48, lineHeight: "28px", boxSizing: "border-box", display: "flex", alignItems: "center" }}>
            {docente ? `${docente.cognome} ${docente.nome}` : <span style={{ color: "#ccc" }}>—</span>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ padding: "0 20px", display: "flex", gap: 2, flexWrap: "wrap", borderBottom: "2px solid #0288d1" }}>
        {TABS.map(t => {
          const active = t.key === tab;
          const n = counts[t.key] || 0;
          return (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              style={{ padding: "10px 22px", background: active ? "#0288d1" : "#e1f5fe", color: active ? "white" : "#0277bd", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5 }}>
              {t.label}{n > 0 && <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.85 }}>({n})</span>}
            </button>
          );
        })}
      </div>

      {msg && <div style={{ margin: "10px 20px 0", padding: "6px 12px", background: msg.ok ? "#eeffee" : "#ffeeee", border: `1px solid ${msg.ok ? "#aea" : "#eaa"}`, fontSize: 12, color: msg.ok ? "#060" : "#c00" }}>{msg.text}</div>}

      {/* Toolbar tab corrente */}
      {!current.isUpload && (
        <div style={{ padding: "10px 20px 0", display: "flex", justifyContent: "flex-end", gap: 6, flexWrap: "wrap" }}>
          {tab === "servizio" && (
            <>
              <button data-testid="btn-gestione-modelli" onClick={() => setShowModelli(true)}
                className="sw-filter-btn" style={{ background: "#5e35b1", color: "white", borderColor: "#5e35b1" }}>
                📋 Gestione modelli
              </button>
              <button data-testid="btn-genera-documento" onClick={() => setShowGenera(true)}
                className="sw-filter-btn" style={{ background: "#ef6c00", color: "white", borderColor: "#ef6c00" }}>
                📄 Genera documento
              </button>
            </>
          )}
          <button data-testid="btn-cleanup-eventi-dup" onClick={cleanupDuplicati}
            className="sw-filter-btn" style={{ background: "#c62828", color: "white", borderColor: "#c62828" }}
            title="Rimuove righe duplicate dagli archivi LAVORO/SERVIZIO/TITOLI/ABILITAZIONI/CORSI">
            🧹 Rimuovi duplicati
          </button>
          <button data-testid="btn-add-row" onClick={addRow} className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#2e7d32" }}>+ Aggiungi riga</button>
        </div>
      )}

      <div style={{ padding: "10px 20px" }}>
        {current.isUpload ? (
          <AllegatiTab codice={codice} onMsg={setMsg} />
        ) : loading ? (
          <div className="sw-loading">Caricamento...</div>
        ) : eventi.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 14, background: "white", border: "1px solid #ddd" }}>
            Nessun evento <b>{current.label}</b> per questo docente. Clicca "+ Aggiungi riga".
          </div>
        ) : (
          <div className="sw-table-container">
            <table className="sw-table">
              <thead>
                <tr>
                  {current.cols.map(c => (
                    <th key={c} onClick={() => toggleSort(c)} style={{ background: "#0288d1", color: "white", fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }} title="Clicca per ordinare">
                      {c.toUpperCase()}{sortInd(c)}
                    </th>
                  ))}
                  <th className="center-col" style={{ background: "#0288d1", color: "white", width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {sortedEventi.map((e, i) => (
                  <tr key={e.ChiavePrimaria || i} data-testid={`evento-row-${i}`}>
                    {current.cols.map(c => (
                      <EditableCell
                        key={c}
                        field={c}
                        value={e[c]}
                        categoria={tab}
                        freeText={FREE_TEXT.has(c)}
                        isDate={DATE_FIELDS.has(c)}
                        onSave={(v) => updateCell(e.ChiavePrimaria, c, v)}
                      />
                    ))}
                    <td className="center-col" style={{ whiteSpace: "nowrap" }}>
                      {tab === "documenti" && e.chiave_allegato && (
                        <>
                          <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docenti/${codice}/allegati/${e.chiave_allegato}/download?inline=1`, "_blank")}
                            title="Anteprima file"
                            data-testid={`btn-doc-preview-${i}`}
                            style={{ background: "white", color: "#0288d1", border: "1px solid #0288d1", padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 4 }}>👁</button>
                          <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docenti/${codice}/allegati/${e.chiave_allegato}/download`, "_blank")}
                            title="Scarica file"
                            data-testid={`btn-doc-download-${i}`}
                            style={{ background: "#0288d1", color: "white", border: "none", padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 4 }}>⬇</button>
                        </>
                      )}
                      <button onClick={() => deleteRow(e.ChiavePrimaria)} title="Elimina riga"
                        style={{ background: "white", color: "#c62828", border: "1px solid #c62828", padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                        data-testid={`btn-del-${i}`}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModelli && (
        <ModelliModal onClose={() => setShowModelli(false)} onMsg={setMsg} />
      )}
      {showGenera && (
        <GeneraDocumentoModal codice={codice} docente={docente} eventi={sortedEventi} onClose={() => setShowGenera(false)} onMsg={setMsg} />
      )}
    </div>
  );
}

function EditableCell({ field, value, categoria, freeText, isDate, onSave }) {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value ?? "");
  const [dirty, setDirty] = useState(false); // true se l'utente ha modificato il valore (abilita filtro dropdown)
  const [options, setOptions] = useState([]);
  const [showList, setShowList] = useState(false);
  const [dateMode, setDateMode] = useState(false);
  const inputRef = useRef(null);

  const display = field === "Anno" ? formatAnno(value) : value;

  useEffect(() => { setTemp(value ?? ""); }, [value]);

  const loadOptions = async () => {
    if (freeText) return;
    const cacheKey = `${field}_${categoria}`;
    if (distinctCache[cacheKey]) { setOptions(distinctCache[cacheKey]); return; }
    try {
      const res = await api.get(`/docenti-eventi/distinct/${encodeURIComponent(field)}`, { params: { categoria } });
      const vals = res.data.values || [];
      distinctCache[cacheKey] = vals;
      setOptions(vals);
    } catch { /* ignore */ }
  };

  const startEdit = () => {
    setEditing(true);
    setTemp(value ?? "");
    setDirty(false);
    if (isDate) {
      const iso = dmyToIso(value);
      setDateMode(!!iso || !value);
      if (iso || !value) setTemp(iso || "");
      else loadOptions();
    } else {
      loadOptions();
      setShowList(!freeText);
    }
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const commit = (valueOverride) => {
    setEditing(false);
    setShowList(false);
    setDateMode(false);
    let final = valueOverride !== undefined ? valueOverride : temp;
    // Conversione ISO -> dd/mm/yyyy in output se dateMode
    if (isDate && valueOverride === undefined && final && /^\d{4}-\d{2}-\d{2}$/.test(final)) {
      final = isoToDmy(final);
    }
    if ((final ?? "") !== (value ?? "")) onSave(final);
  };

  const cancel = () => {
    setEditing(false);
    setShowList(false);
    setDateMode(false);
    setTemp(value ?? "");
  };

  const onKey = (ev) => {
    if (ev.key === "Enter") { ev.preventDefault(); commit(); }
    else if (ev.key === "Escape") { ev.preventDefault(); cancel(); }
  };

  // Se l'utente non ha ancora digitato nulla → mostra TUTTE le opzioni (no filtro).
  // Appena digita (dirty) → filtra con match case-insensitive.
  const filtered = !freeText
    ? (dirty && temp
        ? options.filter(o => String(o).toLowerCase().includes(String(temp).toLowerCase())).slice(0, 100)
        : options.slice(0, 100))
    : [];

  const wide = field === "Nota" || field === "Descrizione" || field === "Titolo" || field === "Corso";

  if (!editing) {
    return (
      <td onClick={startEdit} style={{ whiteSpace: wide ? "normal" : "nowrap", cursor: "text", minWidth: 60 }} title={isDate ? "Clicca per aprire il calendario" : "Clicca per modificare"}>
        {display || <span style={{ color: "#ccc" }}>—</span>}
      </td>
    );
  }

  // Date mode: input type=date con icona calendario nativa
  if (isDate && dateMode) {
    return (
      <td style={{ position: "relative", padding: 0, background: "#fffde7", whiteSpace: "nowrap", minWidth: 140 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <input
            ref={inputRef}
            type="date"
            value={temp || ""}
            onChange={e => setTemp(e.target.value)}
            onBlur={() => setTimeout(() => commit(), 200)}
            onKeyDown={onKey}
            style={{ flex: 1, padding: "4px 6px", fontSize: 12, border: "2px solid #0288d1", background: "#fffde7", boxSizing: "border-box", minWidth: 130 }}
            data-testid={`cell-${field}`}
          />
          <button type="button" onMouseDown={e => { e.preventDefault(); setDateMode(false); setTemp(value ?? ""); loadOptions(); setShowList(true); setTimeout(() => inputRef.current?.focus(), 20); }}
            title="Inserisci a mano (anno, intervallo, ecc.)"
            style={{ border: "none", background: "none", cursor: "pointer", fontSize: 11, color: "#0288d1", padding: "2px 4px" }}>✎</button>
        </div>
      </td>
    );
  }

  return (
    <td style={{ position: "relative", padding: 0, background: "#fffde7", whiteSpace: wide ? "normal" : "nowrap", minWidth: 60 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input
          ref={inputRef}
          type="text"
          value={temp}
          onChange={e => { setTemp(e.target.value); setDirty(true); if (!freeText) setShowList(true); }}
          onBlur={() => setTimeout(() => commit(), 200)}
          onKeyDown={onKey}
          onFocus={() => { if (!freeText) setShowList(true); }}
          style={{ flex: 1, padding: "4px 6px", fontSize: 12, border: "2px solid #0288d1", background: "#fffde7", fontFamily: (field === "Anno" || field === "Ore" || field === "Voto") ? "Consolas, monospace" : undefined, boxSizing: "border-box" }}
          data-testid={`cell-${field}`}
        />
        {isDate && (
          <button type="button" onMouseDown={e => { e.preventDefault(); setDateMode(true); setShowList(false); const iso = dmyToIso(temp); setTemp(iso || ""); setTimeout(() => inputRef.current?.focus(), 20); }}
            title="Apri calendario"
            style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14, color: "#0288d1", padding: "2px 4px" }}>📅</button>
        )}
      </div>
      {!freeText && showList && filtered.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: "white", border: "1px solid #0288d1", borderTop: "none", maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}>
          {filtered.map(o => (
            <div key={o} onMouseDown={() => { setTemp(o); setShowList(false); setEditing(false); setTimeout(() => commit(o), 0); }}
              style={{ padding: "4px 8px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
              onMouseEnter={e => e.currentTarget.style.background = "#e1f5fe"}
              onMouseLeave={e => e.currentTarget.style.background = "white"}>
              {field === "Anno" ? formatAnno(o) : o}
            </div>
          ))}
        </div>
      )}
    </td>
  );
}

function AllegatiTab({ codice, onMsg }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upForm, setUpForm] = useState(null); // { data, descrizione, file } o null
  const [uploading, setUploading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/docenti/${codice}/allegati`);
      setItems(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [codice]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const doUpload = async () => {
    if (!upForm?.file) { onMsg({ ok: false, text: "Seleziona un file" }); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", upForm.file);
    fd.append("data", upForm.data || "");
    fd.append("descrizione", upForm.descrizione || "");
    try {
      await api.post(`/docenti/${codice}/allegati`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpForm(null);
      fetchItems();
      onMsg({ ok: true, text: "Allegato caricato" });
    } catch (err) {
      onMsg({ ok: false, text: err.response?.data?.detail || "Errore upload" });
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (chiave) => {
    try {
      await api.delete(`/docenti/${codice}/allegati/${chiave}`);
      fetchItems();
    } catch (err) { onMsg({ ok: false, text: err.response?.data?.detail || "Errore" }); }
  };

  const updateMeta = async (chiave, patch) => {
    setItems(prev => prev.map(i => i.chiave === chiave ? { ...i, ...patch } : i));
    try { await api.put(`/docenti/${codice}/allegati/${chiave}`, patch); }
    catch (err) { onMsg({ ok: false, text: err.response?.data?.detail || "Errore" }); fetchItems(); }
  };

  const previewable = (filename) => {
    const ext = (filename || "").toLowerCase().split(".").pop();
    return ["pdf", "jpg", "jpeg", "png", "webp", "gif"].includes(ext);
  };

  const download = (chiave) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/docenti/${codice}/allegati/${chiave}/download`;
    window.open(url, "_blank");
  };

  const preview = (chiave) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/docenti/${codice}/allegati/${chiave}/download?inline=1`;
    window.open(url, "_blank");
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: "#666" }}>
          {items.length} allegato{items.length === 1 ? "" : "i"} · Max 10MB per file · Formati: pdf, doc(x), jpg, png, xls(x)
        </span>
        <button data-testid="btn-new-allegato" onClick={() => setUpForm({ data: "", descrizione: "", file: null })}
          className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#2e7d32" }}>
          + Aggiungi allegato
        </button>
      </div>

      {loading ? (
        <div className="sw-loading">Caricamento...</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 14, background: "white", border: "1px solid #ddd" }}>
          Nessun allegato. Clicca "+ Aggiungi allegato".
        </div>
      ) : (
        <div className="sw-table-container">
          <table className="sw-table">
            <thead>
              <tr>
                <th style={{ background: "#0288d1", color: "white", width: 130 }}>DATA</th>
                <th style={{ background: "#0288d1", color: "white" }}>DESCRIZIONE</th>
                <th style={{ background: "#0288d1", color: "white" }}>NOME FILE</th>
                <th style={{ background: "#0288d1", color: "white", width: 100 }}>DIMENSIONE</th>
                <th style={{ background: "#0288d1", color: "white", width: 100 }}>CARICATO</th>
                <th style={{ background: "#0288d1", color: "white", width: 170 }} className="center-col"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.chiave} data-testid={`allegato-${a.chiave}`}>
                  <td>
                    <input type="text" defaultValue={a.data || ""} placeholder="dd/mm/yyyy"
                      onBlur={e => { const v = e.target.value.trim(); if (v !== (a.data || "")) updateMeta(a.chiave, { data: v }); }}
                      style={{ width: "100%", padding: "3px 5px", fontSize: 12, border: "1px solid transparent", background: "transparent" }}
                      onFocus={e => { e.currentTarget.style.border = "1px solid #0288d1"; e.currentTarget.style.background = "#fffde7"; }}
                      onBlurCapture={e => { e.currentTarget.style.border = "1px solid transparent"; e.currentTarget.style.background = "transparent"; }} />
                  </td>
                  <td>
                    <input type="text" defaultValue={a.descrizione || ""}
                      onBlur={e => { const v = e.target.value.trim(); if (v !== (a.descrizione || "")) updateMeta(a.chiave, { descrizione: v }); }}
                      style={{ width: "100%", padding: "3px 5px", fontSize: 12, border: "1px solid transparent", background: "transparent" }}
                      onFocus={e => { e.currentTarget.style.border = "1px solid #0288d1"; e.currentTarget.style.background = "#fffde7"; }}
                      onBlurCapture={e => { e.currentTarget.style.border = "1px solid transparent"; e.currentTarget.style.background = "transparent"; }} />
                  </td>
                  <td style={{ fontFamily: "Consolas, monospace", fontSize: 12 }}>{a.filename}</td>
                  <td style={{ fontFamily: "Consolas, monospace", fontSize: 11, color: "#666" }}>{fmtSize(a.size)}</td>
                  <td style={{ fontSize: 10, color: "#666" }}>{a.data_upload ? new Date(a.data_upload).toLocaleDateString("it-IT") : ""}</td>
                  <td className="center-col" style={{ whiteSpace: "nowrap" }}>
                    <button data-testid={`btn-preview-${a.chiave}`}
                      onClick={() => preview(a.chiave)}
                      disabled={!previewable(a.filename)}
                      title={previewable(a.filename) ? "Anteprima" : "Anteprima non disponibile per questo tipo di file (solo pdf/jpg/png)"}
                      style={{ background: "white", color: previewable(a.filename) ? "#0288d1" : "#bbb", border: `1px solid ${previewable(a.filename) ? "#0288d1" : "#ddd"}`, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: previewable(a.filename) ? "pointer" : "not-allowed", marginRight: 4 }}>👁</button>
                    <button data-testid={`btn-dl-${a.chiave}`} onClick={() => download(a.chiave)} title="Scarica"
                      style={{ background: "#0288d1", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>⬇</button>
                    <button data-testid={`btn-del-allegato-${a.chiave}`} onClick={() => doDelete(a.chiave)} title="Elimina"
                      style={{ background: "white", color: "#c62828", border: "1px solid #c62828", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {upForm && (
        <div onClick={() => !uploading && setUpForm(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="upload-modal" style={{ background: "white", width: "100%", maxWidth: 520, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ background: "#0288d1", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>Nuovo allegato</span>
              <button onClick={() => !uploading && setUpForm(null)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Data</label>
                <input type="date" value={upForm.data} onChange={e => setUpForm(f => ({ ...f, data: e.target.value }))}
                  style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Descrizione</label>
                <input type="text" value={upForm.descrizione} onChange={e => setUpForm(f => ({ ...f, descrizione: e.target.value }))}
                  placeholder="es. Contratto assunzione"
                  style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>File (max 10MB: pdf, doc(x), jpg, png, xls(x))</label>
                <input data-testid="file-input" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                  onChange={e => setUpForm(f => ({ ...f, file: e.target.files[0] || null }))}
                  style={{ width: "100%", padding: "4px", fontSize: 12 }} />
                {upForm.file && <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>{upForm.file.name} · {fmtSize(upForm.file.size)}</div>}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button onClick={() => setUpForm(null)} disabled={uploading} className="sw-filter-btn">Annulla</button>
                <button data-testid="btn-upload-confirm" onClick={doUpload} disabled={uploading || !upForm.file} className="sw-filter-btn sw-filter-btn-primary">
                  {uploading ? "Caricamento..." : "📤 Carica"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ====================== MODELLI (Gestione template .docx) ======================
function ModelliModal({ onClose, onMsg }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // { codice, nome, file } | null
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/docenti-modelli");
      setItems(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const doSave = async () => {
    if (!form?.codice?.trim() || !form?.nome?.trim() || !form?.file) {
      onMsg({ ok: false, text: "Codice, nome e file sono obbligatori" });
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("codice", form.codice.trim());
    fd.append("nome", form.nome.trim());
    try {
      await api.post("/docenti-modelli", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(null);
      fetchItems();
      onMsg({ ok: true, text: "Modello caricato" });
    } catch (err) {
      onMsg({ ok: false, text: err.response?.data?.detail || "Errore caricamento modello" });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (chiave) => {
    try {
      await api.delete(`/docenti-modelli/${chiave}`);
      fetchItems();
    } catch (err) {
      onMsg({ ok: false, text: err.response?.data?.detail || "Errore" });
    }
  };

  const doDownload = (chiave) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/docenti-modelli/${chiave}/download`;
    window.open(url, "_blank");
  };

  const updateField = async (chiave, field, newValue, originalValue) => {
    const val = (newValue || "").trim();
    if (val === (originalValue || "")) return;
    if (!val) {
      onMsg({ ok: false, text: `Il campo "${field}" non può essere vuoto` });
      fetchItems();
      return;
    }
    // Optimistic update
    setItems(prev => prev.map(i => i.chiave === chiave ? { ...i, [field]: field === "codice" ? val.toUpperCase() : val } : i));
    try {
      await api.put(`/docenti-modelli/${chiave}`, { [field]: val });
      onMsg({ ok: true, text: `${field === "codice" ? "Codice" : "Nome"} aggiornato` });
    } catch (err) {
      onMsg({ ok: false, text: err.response?.data?.detail || "Errore salvataggio" });
      fetchItems();
    }
  };

  const fmtSize = (b) => !b ? "" : b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(2)} MB`;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="modelli-modal" style={{ background: "white", width: "100%", maxWidth: 900, maxHeight: "90vh", overflow: "auto", borderRadius: 4 }}>
        <div style={{ background: "#5e35b1", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700 }}>Gestione Modelli .docx</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: "#666" }}>{items.length} {items.length === 1 ? "modello" : "modelli"}</span>
            <button data-testid="btn-new-modello" onClick={() => setForm({ codice: "", nome: "", file: null })}
              className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#2e7d32" }}>+ Nuovo modello</button>
          </div>

          {loading ? <div className="sw-loading">Caricamento...</div> : items.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 13, border: "1px solid #ddd" }}>
              Nessun modello. Carica un file .docx con MERGEFIELD (es. «ph_CognNom», «T_anno»...).
            </div>
          ) : (
            <div className="sw-table-container">
              <table className="sw-table">
                <thead>
                  <tr>
                    <th style={{ background: "#5e35b1", color: "white", width: 120 }}>CODICE</th>
                    <th style={{ background: "#5e35b1", color: "white" }}>NOME</th>
                    <th style={{ background: "#5e35b1", color: "white" }}>FILE</th>
                    <th style={{ background: "#5e35b1", color: "white", width: 90 }}>DIMENS.</th>
                    <th style={{ background: "#5e35b1", color: "white", width: 90 }}>CAMPI</th>
                    <th style={{ background: "#5e35b1", color: "white", width: 140 }} className="center-col"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(m => (
                    <tr key={m.chiave} data-testid={`modello-${m.chiave}`}>
                      <td style={{ padding: 0 }}>
                        <input type="text" defaultValue={m.codice || ""}
                          key={`cod-${m.chiave}-${m.codice}`}
                          data-testid={`in-edit-codice-${m.chiave}`}
                          onBlur={e => updateField(m.chiave, "codice", e.target.value, m.codice)}
                          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { e.target.value = m.codice || ""; e.target.blur(); } }}
                          style={{ width: "100%", padding: "5px 7px", fontSize: 13, border: "1px solid transparent", background: "transparent", fontFamily: "Consolas, monospace", fontWeight: 700, textTransform: "uppercase", boxSizing: "border-box" }}
                          onFocus={e => { e.currentTarget.style.border = "1px solid #5e35b1"; e.currentTarget.style.background = "#fffde7"; }}
                          onBlurCapture={e => { e.currentTarget.style.border = "1px solid transparent"; e.currentTarget.style.background = "transparent"; }} />
                      </td>
                      <td style={{ padding: 0 }}>
                        <input type="text" defaultValue={m.nome || ""}
                          key={`nom-${m.chiave}-${m.nome}`}
                          data-testid={`in-edit-nome-${m.chiave}`}
                          onBlur={e => updateField(m.chiave, "nome", e.target.value, m.nome)}
                          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { e.target.value = m.nome || ""; e.target.blur(); } }}
                          style={{ width: "100%", padding: "5px 7px", fontSize: 13, border: "1px solid transparent", background: "transparent", boxSizing: "border-box" }}
                          onFocus={e => { e.currentTarget.style.border = "1px solid #5e35b1"; e.currentTarget.style.background = "#fffde7"; }}
                          onBlurCapture={e => { e.currentTarget.style.border = "1px solid transparent"; e.currentTarget.style.background = "transparent"; }} />
                      </td>
                      <td style={{ fontFamily: "Consolas, monospace", fontSize: 12 }}>{m.filename}</td>
                      <td style={{ fontFamily: "Consolas, monospace", fontSize: 11, color: "#666" }}>{fmtSize(m.size)}</td>
                      <td style={{ textAlign: "center", fontSize: 11 }} title={(m.merge_fields || []).join(", ")}>{(m.merge_fields || []).length}</td>
                      <td className="center-col" style={{ whiteSpace: "nowrap" }}>
                        <button onClick={() => doDownload(m.chiave)} title="Scarica template"
                          style={{ background: "#5e35b1", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>⬇</button>
                        <button data-testid={`btn-del-modello-${m.chiave}`} onClick={() => doDelete(m.chiave)} title="Elimina"
                          style={{ background: "white", color: "#c62828", border: "1px solid #c62828", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {form && (
            <div style={{ marginTop: 16, padding: 14, background: "#f3e5f5", border: "1px solid #ce93d8" }} data-testid="modello-form">
              <div style={{ fontWeight: 700, color: "#5e35b1", marginBottom: 10 }}>Nuovo modello</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Codice modello</label>
                  <input data-testid="in-modello-codice" type="text" value={form.codice} onChange={e => setForm(f => ({ ...f, codice: e.target.value }))}
                    placeholder="CS_PRIM"
                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box", fontFamily: "Consolas, monospace", textTransform: "uppercase" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Nome modello</label>
                  <input data-testid="in-modello-nome" type="text" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                    placeholder="Certificato di servizio Primaria"
                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>File di stampa (.docx con MERGEFIELD)</label>
                <input data-testid="in-modello-file" type="file" accept=".docx"
                  onChange={e => setForm(f => ({ ...f, file: e.target.files[0] || null }))}
                  style={{ width: "100%", padding: "4px", fontSize: 12 }} />
                {form.file && <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>{form.file.name} · {fmtSize(form.file.size)}</div>}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button onClick={() => setForm(null)} disabled={saving} className="sw-filter-btn">Annulla</button>
                <button data-testid="btn-save-modello" onClick={doSave} disabled={saving || !form.file} className="sw-filter-btn sw-filter-btn-primary">
                  {saving ? "Caricamento..." : "💾 Salva modello"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ====================== GENERA DOCUMENTO ======================
function GeneraDocumentoModal({ codice, docente, eventi, onClose, onMsg }) {
  const [modelli, setModelli] = useState([]);
  const [loadingModelli, setLoadingModelli] = useState(true);
  const [selectedModello, setSelectedModello] = useState("");
  const [selected, setSelected] = useState(() => new Set(eventi.map(e => e.ChiavePrimaria)));
  const [scuola, setScuola] = useState("Istituto Canossiano");
  const [regcert, setRegcert] = useState("");
  const [dataDoc, setDataDoc] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
  });
  const [generating, setGenerating] = useState(false);
  const [dateMode, setDateMode] = useState(false);  // false = text dd/mm/yyyy, true = native date picker
  const [scuoleList, setScuoleList] = useState([]);
  const [showEditScuole, setShowEditScuole] = useState(false);
  const [scuoleDraft, setScuoleDraft] = useState("");
  const [savingScuole, setSavingScuole] = useState(false);

  const fetchScuole = useCallback(async () => {
    try {
      const r = await api.get("/docenti-config/scuole");
      setScuoleList(r.data.values || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchScuole(); }, [fetchScuole]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/docenti-modelli");
        setModelli(r.data.data || []);
        if ((r.data.data || []).length === 1) setSelectedModello(r.data.data[0].chiave);
      } catch { /* ignore */ } finally { setLoadingModelli(false); }
    })();
  }, []);

  const toggleEvento = (k) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === eventi.length) setSelected(new Set());
    else setSelected(new Set(eventi.map(e => e.ChiavePrimaria)));
  };

  const doGenerate = async (format, inline) => {
    if (!selectedModello) { onMsg({ ok: false, text: "Seleziona un modello" }); return; }
    const orderedChiavi = eventi.filter(e => selected.has(e.ChiavePrimaria)).map(e => e.ChiavePrimaria);
    setGenerating(true);
    try {
      const res = await api.post(`/docenti/${codice}/genera-documento`, {
        modello_id: selectedModello,
        eventi_chiavi: orderedChiavi,
        scuola, regcert, data: dataDoc,
      }, { responseType: "blob", params: { format, inline: inline ? 1 : 0 } });
      let fname = `documento.${format}`;
      const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"];
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
        if (m) fname = decodeURIComponent(m[1]);
      }
      const mime = format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      if (inline) {
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        onMsg({ ok: true, text: "Anteprima aperta in una nuova scheda" });
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = fname;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        onMsg({ ok: true, text: `Documento "${fname}" scaricato` });
        onClose();
      }
    } catch (err) {
      let detail = "Errore generazione";
      try {
        if (err.response?.data) {
          const text = err.response.data instanceof Blob ? await err.response.data.text() : JSON.stringify(err.response.data);
          try { detail = JSON.parse(text).detail || detail; } catch { detail = text || detail; }
        }
      } catch { /* ignore */ }
      onMsg({ ok: false, text: detail });
    } finally {
      setGenerating(false);
    }
  };

  const doArchivia = async () => {
    if (!selectedModello) { onMsg({ ok: false, text: "Seleziona un modello" }); return; }
    const orderedChiavi = eventi.filter(e => selected.has(e.ChiavePrimaria)).map(e => e.ChiavePrimaria);
    const selMod2 = modelli.find(m => m.chiave === selectedModello);
    const defaultDesc = `${selMod2?.nome || selMod2?.codice || "Documento"} — ${dataDoc}`;
    setGenerating(true);
    try {
      const res = await api.post(`/docenti/${codice}/archivia-documento`, {
        modello_id: selectedModello,
        eventi_chiavi: orderedChiavi,
        scuola, regcert, data: dataDoc,
        descrizione: defaultDesc,
      });
      const fname = res.data?.allegato?.filename || "documento.docx";
      onMsg({ ok: true, text: `Documento archiviato in DOCUMENTI: ${fname}` });
      onClose();
    } catch (err) {
      onMsg({ ok: false, text: err.response?.data?.detail || "Errore archiviazione" });
    } finally {
      setGenerating(false);
    }
  };

  const selMod = modelli.find(m => m.chiave === selectedModello);

  const saveScuoleList = async () => {
    const lines = scuoleDraft.split("\n").map(s => s.trim()).filter(Boolean);
    setSavingScuole(true);
    try {
      const r = await api.put("/docenti-config/scuole", { values: lines });
      const saved = r.data.values || lines;
      setScuoleList(saved);
      setShowEditScuole(false);
      onMsg({ ok: true, text: `Lista scuole aggiornata (${saved.length} valori)` });
    } catch (err) {
      onMsg({ ok: false, text: err.response?.data?.detail || "Errore salvataggio lista" });
    } finally {
      setSavingScuole(false);
    }
  };

  return (
    <div onClick={generating ? null : onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="genera-modal" style={{ background: "white", width: "100%", maxWidth: 1000, maxHeight: "90vh", overflow: "auto", borderRadius: 4 }}>
        <div style={{ background: "#ef6c00", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700 }}>Genera documento · {docente ? `${docente.cognome} ${docente.nome}` : `Docente ${codice}`}</span>
          <button onClick={onClose} disabled={generating} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Modello *</label>
              <select data-testid="sel-modello" value={selectedModello} onChange={e => setSelectedModello(e.target.value)}
                disabled={loadingModelli}
                style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box" }}>
                <option value="">{loadingModelli ? "Caricamento..." : "— Seleziona —"}</option>
                {modelli.map(m => <option key={m.chiave} value={m.chiave}>{m.codice} — {m.nome}</option>)}
              </select>
              {selMod && (
                <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                  Campi rilevati: {(selMod.merge_fields || []).length}
                </div>
              )}
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Data certificato</label>
              <div style={{ display: "flex", alignItems: "stretch", gap: 4 }}>
                {dateMode ? (
                  <input data-testid="in-data-cert-picker" type="date" autoFocus
                    value={dmyToIso(dataDoc) || ""}
                    onChange={e => { setDataDoc(isoToDmy(e.target.value)); }}
                    onBlur={() => setDateMode(false)}
                    style={{ flex: 1, padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box", fontFamily: "Consolas, monospace" }} />
                ) : (
                  <input data-testid="in-data-cert" type="text" value={dataDoc} onChange={e => setDataDoc(e.target.value)} placeholder="dd/mm/yyyy"
                    style={{ flex: 1, padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box", fontFamily: "Consolas, monospace" }} />
                )}
                <button type="button" data-testid="btn-cal-data-cert" onClick={() => setDateMode(m => !m)}
                  title={dateMode ? "Torna a testo libero" : "Apri calendario"}
                  style={{ border: "1px solid #bbb", background: dateMode ? "#ef6c00" : "white", color: dateMode ? "white" : "#ef6c00", padding: "0 10px", cursor: "pointer", fontSize: 14, borderRadius: 0 }}>
                  📅
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Reg. Certificato</label>
              <input type="text" value={regcert} onChange={e => setRegcert(e.target.value)} placeholder="es. 5464 A.S. 2025/26"
                style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Scuola</label>
            <div style={{ display: "flex", alignItems: "stretch", gap: 4 }}>
              <input data-testid="in-scuola" type="text" list="scuole-datalist" value={scuola} onChange={e => setScuola(e.target.value)}
                placeholder="Seleziona o digita..."
                style={{ flex: 1, padding: "6px 8px", fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box" }} />
              <datalist id="scuole-datalist">
                {scuoleList.map(s => <option key={s} value={s} />)}
              </datalist>
              <button type="button" data-testid="btn-edit-scuole" onClick={() => { setScuoleDraft(scuoleList.join("\n")); setShowEditScuole(true); }}
                title="Modifica lista scuole"
                style={{ border: "1px solid #bbb", background: "white", color: "#ef6c00", padding: "0 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, borderRadius: 0 }}>
                ✎ Modifica lista
              </button>
            </div>
            {scuoleList.length > 0 && (
              <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                {scuoleList.length} valori disponibili — click sul campo per scegliere
              </div>
            )}
          </div>

          <div style={{ fontWeight: 700, color: "#ef6c00", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Eventi Servizio del docente ({eventi.length}) — seleziona quelli da includere</span>
            <button onClick={toggleAll} className="sw-filter-btn" style={{ fontSize: 11 }} data-testid="btn-toggle-all">
              {selected.size === eventi.length ? "Deseleziona tutti" : "Seleziona tutti"}
            </button>
          </div>

          {eventi.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 13, border: "1px solid #ddd" }}>
              Nessun evento Servizio presente. Aggiungi prima le righe nel tab SERVIZIO.
            </div>
          ) : (
            <div className="sw-table-container" style={{ maxHeight: 320, overflowY: "auto" }}>
              <table className="sw-table">
                <thead>
                  <tr>
                    <th style={{ background: "#ef6c00", color: "white", width: 40 }}></th>
                    <th style={{ background: "#ef6c00", color: "white" }}>ANNO</th>
                    <th style={{ background: "#ef6c00", color: "white" }}>DAL</th>
                    <th style={{ background: "#ef6c00", color: "white" }}>AL</th>
                    <th style={{ background: "#ef6c00", color: "white" }}>SETTORE</th>
                    <th style={{ background: "#ef6c00", color: "white" }}>CLASSE</th>
                    <th style={{ background: "#ef6c00", color: "white" }}>ORE</th>
                    <th style={{ background: "#ef6c00", color: "white" }}>MATERIA</th>
                  </tr>
                </thead>
                <tbody>
                  {eventi.map(e => {
                    const checked = selected.has(e.ChiavePrimaria);
                    return (
                      <tr key={e.ChiavePrimaria} data-testid={`gen-row-${e.ChiavePrimaria}`}
                        onClick={() => toggleEvento(e.ChiavePrimaria)}
                        style={{ background: checked ? "#fff3e0" : "white", cursor: "pointer" }}>
                        <td className="center-col"><input type="checkbox" checked={checked} onChange={() => toggleEvento(e.ChiavePrimaria)} onClick={ev => ev.stopPropagation()} /></td>
                        <td>{formatAnno(e.Anno)}</td>
                        <td>{e.Dal || ""}</td>
                        <td>{e.Al || ""}</td>
                        <td>{e.Settore || ""}</td>
                        <td>{e.Classe || ""}</td>
                        <td>{e.Ore || ""}</td>
                        <td>{e.Materia || ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
            <span style={{ fontSize: 12, color: "#666" }}>{selected.size} evento{selected.size === 1 ? "" : "i"} selezionato{selected.size === 1 ? "" : "i"}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} disabled={generating} className="sw-filter-btn">Annulla</button>
              <button data-testid="btn-anteprima" onClick={() => doGenerate("pdf", true)} disabled={generating || !selectedModello}
                className="sw-filter-btn" title="Apri PDF di anteprima in una nuova scheda"
                style={{ background: "white", color: "#0288d1", border: "1px solid #0288d1", fontWeight: 700 }}>
                👁 Anteprima
              </button>
              <button data-testid="btn-salva-docx" onClick={() => doGenerate("docx", false)} disabled={generating || !selectedModello}
                className="sw-filter-btn" style={{ background: "#5e35b1", color: "white", border: "1px solid #5e35b1", fontWeight: 700 }}>
                💾 Salva .docx
              </button>
              <button data-testid="btn-salva-pdf" onClick={() => doGenerate("pdf", false)} disabled={generating || !selectedModello}
                className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#ef6c00", borderColor: "#ef6c00" }}>
                {generating ? "Generazione..." : "📄 Salva .pdf"}
              </button>
              <button data-testid="btn-archivia" onClick={doArchivia} disabled={generating || !selectedModello}
                className="sw-filter-btn" title="Salva il .docx come allegato e crea una riga nel tab DOCUMENTI"
                style={{ background: "#2e7d32", color: "white", border: "1px solid #2e7d32", fontWeight: 700 }}>
                📁 Archivia
              </button>
            </div>
          </div>
        </div>
      </div>

      {showEditScuole && (
        <div onClick={() => !savingScuole && setShowEditScuole(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="edit-scuole-modal"
            style={{ background: "white", width: "100%", maxWidth: 640, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ background: "#ef6c00", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>Modifica lista Scuole</span>
              <button onClick={() => !savingScuole && setShowEditScuole(false)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>
                Un valore per riga. Le righe vuote sono ignorate.
              </label>
              <textarea data-testid="ta-scuole" value={scuoleDraft} onChange={e => setScuoleDraft(e.target.value)}
                rows={10}
                style={{ width: "100%", padding: 8, fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box", fontFamily: "Consolas, monospace", resize: "vertical" }} />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                <button onClick={() => setShowEditScuole(false)} disabled={savingScuole} className="sw-filter-btn">Annulla</button>
                <button data-testid="btn-save-scuole" onClick={saveScuoleList} disabled={savingScuole}
                  className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#ef6c00", borderColor: "#ef6c00" }}>
                  {savingScuole ? "Salvataggio..." : "💾 Salva lista"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

